import { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Copy, Trash2, ChefHat, Info, Carrot, ListOrdered, Calculator, Printer, Layers } from "lucide-react";
import { brl, pct } from "@/lib/format";
import { YIELD_UNITS } from "@/lib/recipeCalc";
import useRecipeForm, { copyForm } from "@/hooks/useRecipeForm";
import { Card, ErrorState, Field, Input, Loading, Page, PageHeader, StickyFooter, Switch } from "@/components/kit";
import { RecipeTour } from "@/components/recipes/RecipeTour";
import IngredientsEditor from "@/components/recipes/IngredientsEditor";
import FinancialSummary from "@/components/recipes/FinancialSummary";
import PrintDialog from "@/components/recipes/PrintDialog";
import { StepsEditor, PhotoField } from "@/components/recipes/RecipeParts";
import { SelectCreate } from "@/components/SelectCreate";
import { FormActions } from "@/components/FormActions";

const TABS = [
  { id: "general", label: "Dados gerais", icon: Info },
  { id: "ingredients", label: "Insumos", icon: Carrot },
  { id: "steps", label: "Preparo", icon: ListOrdered },
  { id: "finance", label: "Financeiro", icon: Calculator },
];

function TabBar({ tab, setTab, counts }) {
  return (
    <div className="scroll-x mb-5" role="tablist">
      {TABS.map((t) => (
        <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className={`chip !h-10 ${tab === t.id ? "chip-active" : ""}`} onClick={() => setTab(t.id)} data-testid={`recipe-tab-${t.id}`}>
          <t.icon size={15} /> {t.label}{counts[t.id] !== undefined ? ` (${counts[t.id]})` : ""}
        </button>
      ))}
    </div>
  );
}

function BaseSection({ f, setF, usedIn }) {
  return (
    <div className="rounded-xl p-4 flex flex-col gap-3" style={{ background: "var(--surface-alt)" }} data-testid="recipe-base-section">
      <div className="flex items-center gap-3">
        <Layers size={18} style={{ color: "var(--brand)" }} />
        <div className="flex-1"><div className="label">Preparo base</div><div className="caption">Molhos, massas, caldos · pode ser usado como ingrediente de outras fichas</div></div>
        <Switch on={f.is_base} onChange={(v) => setF({ ...f, is_base: v })} data-testid="recipe-base-switch" />
      </div>
      <div className="grid grid-cols-[1fr_110px] gap-3">
        <Field label="Rendimento total (peso, volume ou unidades)" hint={f.is_base ? "Permite usar o preparo por kg, litro ou unidade. Sem isso, só por porção." : "Opcional"}>
          <Input inputMode="decimal" placeholder="Ex.: 2,5" value={f.yield_qty} onChange={(e) => setF({ ...f, yield_qty: e.target.value })} data-testid="recipe-yield-qty-input" />
        </Field>
        <Field label="Unidade">
          <select className="input" value={f.yield_unit} onChange={(e) => setF({ ...f, yield_unit: e.target.value })} data-testid="recipe-yield-unit-select">{YIELD_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}</select>
        </Field>
      </div>
      {usedIn?.length > 0 && <p className="caption" data-testid="recipe-used-in">Usado em: {usedIn.map((r) => r.name).join(", ")}</p>}
    </div>
  );
}

function GeneralTab({ form }) {
  const { f, setF } = form;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Card className="flex flex-col gap-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nome do prato"><Input placeholder="Ex.: Risoto de cogumelos" value={f.name} onChange={set("name")} data-testid="recipe-name-input" /></Field>
        <SelectCreate label="Categoria no cardápio" endpoint="/menu-categories" valueKey="name" value={f.menu_category} onChange={(v) => setF({ ...f, menu_category: v })} allowNone testid="recipe-category-select" />
        <Field label="Tempo de preparo (min)"><Input inputMode="numeric" placeholder="30" value={f.prep_time_min} onChange={(e) => setF({ ...f, prep_time_min: e.target.value.replace(/\D/g, "") })} data-testid="recipe-time-input" /></Field>
        <Field label="Rendimento (porções)"><Input inputMode="decimal" placeholder="1" value={f.yield_portions} onChange={set("yield_portions")} data-testid="recipe-yield-input" /></Field>
        <Field label="Código no caixa / PDV" hint="Usado para reconhecer o prato na planilha de vendas"><Input placeholder="Ex.: 1023" value={f.sales_code} onChange={set("sales_code")} data-testid="recipe-sales-code-input" /></Field>
      </div>
      {!f.is_base && <Field label="Descrição no cardápio" hint="Texto que o cliente lê no cardápio em PDF · até 400 caracteres"><textarea className="input" rows={2} maxLength={400} placeholder="Ex.: Risoto cremoso de cogumelos frescos, finalizado com parmesão e azeite trufado" value={f.menu_description || ""} onChange={set("menu_description")} data-testid="recipe-menu-description-input" /></Field>}
      <BaseSection f={f} setF={setF} usedIn={form.data?.used_in} />
      <PhotoField label="Foto do prato finalizado" path={f.photo_path} onChange={(p) => setF({ ...f, photo_path: p })} testid="recipe-photo" />
    </Card>
  );
}

function StepsTab({ form }) {
  const { f, setF } = form;
  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-3"><div className="h3">Modo de preparo · passo a passo</div><StepsEditor steps={form.steps} setSteps={form.setSteps} /></Card>
      <Card className="flex flex-col gap-4">
        <div className="h3">Empratamento e montagem</div>
        <Field label="Instruções"><textarea className="input" rows={4} placeholder="Ex.: prato fundo branco, risoto ao centro, finalizar com parmesão e salsinha" value={f.plating_notes} onChange={(e) => setF({ ...f, plating_notes: e.target.value })} data-testid="recipe-plating-input" /></Field>
        <PhotoField label="Foto padrão de montagem" path={f.plating_photo_path} onChange={(p) => setF({ ...f, plating_photo_path: p })} testid="recipe-plating-photo" />
      </Card>
    </div>
  );
}

function MiniSummary({ form }) {
  const t = form.totals;
  return (
    <div className="card card-soft flex flex-wrap gap-x-6 gap-y-1 text-sm mb-5" data-testid="recipe-mini-summary">
      <span>Custo total <b className="tnum">{brl(t.total)}</b></span>
      <span>Por porção <b className="tnum">{brl(t.perPortion)}</b></span>
      <span>CMV <b className="tnum">{pct(t.cmvPct)}</b></span>
      {t.suggested !== null && <span>Preço sugerido <b className="tnum">{brl(t.suggested)}</b></span>}
    </div>
  );
}

function FinanceTab({ form }) {
  const { f, setF } = form;
  const yq = Number(String(f.yield_qty).replace(",", "."));
  return (
    <div className="max-w-2xl">
      <FinancialSummary totals={form.totals} portions={f.yield_portions} price={f.sale_price} setPrice={(v) => setF({ ...f, sale_price: v })}
        target={form.target} unitTarget={form.unitTarget} ownTarget={f.own_cmv_target} setOwnTarget={(v) => setF({ ...f, own_cmv_target: v })}
        yieldInfo={yq > 0 ? { qty: yq, unit: f.yield_unit } : null} />
    </div>
  );
}

export default function RecipeForm() {
  const { id } = useParams();
  const nav = useNavigate();
  const form = useRecipeForm(id);
  const loc = useLocation();
  const [tab, setTab] = useState("general");
  const [printing, setPrinting] = useState(false);
  if (form.loading) return <Loading />;
  if (form.error) return <Page><PageHeader title="Ficha técnica" back="/recipes" /><ErrorState message={form.error} /></Page>;
  const right = (
    <div className="flex gap-2">
      <RecipeTour isNew={!id} />
      {id && <>
        <button className="icon-btn" title="Imprimir / PDF" onClick={() => setPrinting(true)} data-testid="print-recipe-button"><Printer size={17} /></button>
        <button className="icon-btn" title="Modo cozinha" onClick={() => nav(`/recipes/${id}/kitchen`)} data-testid="open-kitchen-view-button"><ChefHat size={17} /></button>
        <button className="icon-btn" title="Duplicar · criar tamanho ou versão" onClick={() => nav("/recipes/new", { state: { copy: copyForm(form.f, form.ingredients, form.steps) } })} data-testid="duplicate-recipe-button"><Copy size={16} /></button>
        <button className="icon-btn" title="Excluir" onClick={() => window.confirm("Excluir esta ficha técnica?") && form.del.mutate()} data-testid="delete-recipe-button"><Trash2 size={16} /></button>
      </>}
    </div>
  );
  return (
    <Page>
      <PageHeader title={id ? form.f.name || "Ficha técnica" : loc.state?.copy ? "Nova ficha técnica (cópia)" : "Nova ficha técnica"} back="/recipes" right={right} />
      <TabBar tab={tab} setTab={setTab} counts={{ ingredients: form.ingredients.length, steps: form.steps.filter((s) => s.text.trim()).length }} />
      {tab !== "finance" && <MiniSummary form={form} />}
      {tab === "general" && <GeneralTab form={form} />}
      {tab === "ingredients" && <Card><IngredientsEditor ingredients={form.ingredients} setIngredients={form.setIngredients} sources={form.sources} /></Card>}
      {tab === "steps" && <StepsTab form={form} />}
      {tab === "finance" && <FinanceTab form={form} />}
      <StickyFooter>
        <FormActions testid="recipe" saveLabel="Salvar ficha" saving={form.save.isPending ? (form.save.variables ? "new" : "save") : null} dirty={form.dirty}
          onSave={() => form.submit(false)} onSaveNew={id ? null : () => form.submit(true)} onBack={() => nav("/recipes")} />
      </StickyFooter>
      {form.data && <PrintDialog open={printing} onOpenChange={setPrinting} recipes={[form.data]} />}
    </Page>
  );
}
